import AddIcon from '@mui/icons-material/Add'
import CheckIcon from '@mui/icons-material/Check'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import * as Sentry from '@sentry/browser'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useBookingContext } from '@providers/booking'
import { getSelectedGroom } from '@providers/booking/helper'
import { TBookingPet } from '@providers/booking/types'
import { useSessionContext } from '@providers/session'
import { isUserLoggedIn } from '@providers/session/helpers'
import { buildSearchParams } from '@shared/header_navigation'
import { i18WithDefault as t } from '@shared/locale'
import { useRoutes } from 'app/use_routes'
import { DiscountTag } from 'components-ui/src/discount_tag/discount_tag'
import { GroomNotice } from 'components-ui/src/groom_notice/groom_notice'
import { formatNumberAsPrice } from 'components-ui/src/shared/common'
import { Spacer } from 'components-ui/src/spacer/spacer'
import { ClientBookingSearchService } from 'service-api/src/services/client_booking_search.service'
import { BookingSearchByDaysClientDto } from 'service-api/src/types/api'
import { TBookingSearchOptions } from 'service-api/src/types/general'
import { getPriceRangeForOffersByDay } from '../addon_item_picker/helper'
import { PetCard } from './pet_card'

export interface TGroomingSelectorProps {
  groom: BookingSearchByDaysClientDto
  groupIdx: number
  petsInGroup: TBookingPet[]
}

export const GroomingSelector = ({ groom, groupIdx, petsInGroup }: TGroomingSelectorProps) => {
  const bookingCtx = useBookingContext()
  const ctx = useSessionContext()
  const { accountName: locationName = '', serviceTypeName = '' } = useParams()
  const offer = groom.offer!
  const [addons, setAddons] = useState<BookingSearchByDaysClientDto[] | null>(null)

  const groomingSearchParams = buildSearchParams(bookingCtx, true, true, offer.id)
  const { petGroups } = groomingSearchParams
  const { navigateRoute } = useRoutes()

  const isSelected = useMemo(() => {
    return !!getSelectedGroom(bookingCtx.state, groupIdx, offer.id || '')?.id
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupIdx, bookingCtx.state.cart.groups[groupIdx].grooms])

  useEffect(() => {
    if (isSelected && addons === null) {
      ClientBookingSearchService.searchByDays(locationName, 'grooming', {
        ...(groomingSearchParams as TBookingSearchOptions),
        offersType: 'PET',
      }).then((data) => setTimeout(() => setAddons(data.results), 0))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelected])

  const { minRate, maxRate } = useMemo(() => getPriceRangeForOffersByDay(groom.availabilityGroupsByDays), [groom])

  const getSearchGroupIdx = (petId: string) => {
    const petIdx = petGroups.findIndex((g) => g.pets?.find((p) => p.locationPetProfileId === petId))
    if (petIdx >= 0) {
      return petIdx
    }

    // Throw an error if user is logged in and can't find a pet
    if (isUserLoggedIn(ctx.state.user)) {
      enqueueSnackbar(t(`global.label.unexpectedError`))
      Sentry.captureException(
        `Unable to find pet (ID: ${petId}) in search groups for user (ID: ${ctx.state.user?.id}). Redirecting to service type selection.`
      )

      setTimeout(() => navigateRoute('searchServiceType', { serviceTypeName }), 0)
    }
    return undefined
  }

  const onSelectItem = () => {
    bookingCtx.state.cart.groups[groupIdx].pets.map((_pet, i) => {
      setTimeout(
        () =>
          bookingCtx.dispatch({
            type: 'TOGGLE_GROOM_IN_PET_GROOM',
            payload: { value: groom, groupIdx: groupIdx, petIdx: i },
          }),
        0
      )
    })
  }

  const togglePetInGroom = (petIdx: number) => {
    bookingCtx.dispatch({
      type: 'TOGGLE_GROOM_IN_PET_GROOM',
      payload: { value: groom, groupIdx: groupIdx, petIdx: petIdx },
    })
  }

  const BtnIcon = useMemo(() => (isSelected ? CheckIcon : AddIcon), [isSelected])
  const priceVaries = offer?.priceVaries

  return (
    <Box>
      <Stack direction="row" alignItems="start" spacing={3} sx={{ margin: '0 auto', width: '100%' }}>
        <Stack direction={{ xs: 'row', md: 'row' }} width="100%" spacing={1} gap={2} justifyContent={'space-between'}>
          <Stack direction="column" spacing={1}>
            <Stack sx={{ flexDirection: { xs: 'column', sm: 'row' } }} alignItems="start">
              <Typography flexGrow={1} variant="h6">
                {offer.displayName}
              </Typography>
              <DiscountTag
                discounts={groom.availabilityGroupsByDays?.[groupIdx]?.[0].availabilities?.[0]?.price?.discounts}
                cssOverrides={{ maxWidth: '150px', mb: 1, ml: 2 }}
                eligibleForConsumableBenefits={groom.eligibleForConsumableBenefits}
              />
            </Stack>
            <Typography flexGrow={1} variant="body2">
              {offer.description}
            </Typography>
          </Stack>

          <Stack direction="column" spacing={1} sx={{}}>
            {minRate && maxRate && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: { xs: 'flex-end', sm: 'flex-end' },
                  flexWrap: 'wrap',
                  gap: '4px',
                }}
              >
                <Typography variant="body2" component="span" sx={{ paddingRight: '4px' }}>
                  {priceVaries && t('offers.startingAt')}
                </Typography>
                <Typography variant="body2" component="span">
                  <strong>
                    {minRate === maxRate
                      ? formatNumberAsPrice(minRate)
                      : `${formatNumberAsPrice(minRate)} - ${formatNumberAsPrice(maxRate)}`}
                  </strong>
                </Typography>
                <Typography variant="body2" component="span">
                  /{t('global.label.each')}
                </Typography>
              </Box>
            )}
            <Button
              variant={isSelected ? 'contained' : 'outlined'}
              size="small"
              startIcon={<BtnIcon />}
              onClick={onSelectItem}
              sx={{
                minWidth: '120px',
                py: 1,
                alignSelf: { xs: 'flex-end', md: 'flex-end' },
              }}
            >
              {isSelected ? t('global.label.added') : t('global.label.add')}
            </Button>
          </Stack>
        </Stack>
      </Stack>

      <Spacer height={1} />
      {isSelected && (
        <Box sx={{ margin: '0 auto', width: '100%', mt: 0.5, mb: 0.5 }}>
          <GroomNotice />
        </Box>
      )}
      {isSelected && addons === null && <Skeleton variant="rounded" height="50px" sx={{ margin: '0 auto' }} />}
      {isSelected &&
        addons !== null &&
        petsInGroup.map((pet, petIdx) => {
          const searchGroupIdx = getSearchGroupIdx(pet.id) ?? petIdx
          return (
            <PetCard
              petIdx={petIdx}
              key={`${pet.id}_${groom.offer!.id}`}
              pet={pet}
              groupIdx={groupIdx}
              groom={groom}
              togglePetInGroom={(petIdx) => togglePetInGroom(petIdx)}
              addons={addons}
              searchGroupIdx={searchGroupIdx}
            />
          )
        })}
    </Box>
  )
}
